// Result tables use the actual missed-approach phases. Map geometries and
// procedure totals retain the backend's combined segments/controlling rows.
// Presence inside a protection area is independent of critical/penetration flags.
export function gnssObstacleCounts(result) {
  const assessed = new Set();
  const critical = new Set();
  for (const segment of result?.segments || []) {
    const groups = [segment.obsRows,
      ...Object.values(segment.missedApproach?.phaseResults || {})];
    for (const rows of groups) {
      if (!Array.isArray(rows)) continue;
      for (const row of rows) {
        // The same source obstacle can have many sampled points and segment rows.
        // Names are only a compatibility fallback for older API responses.
        const key = row.obstacleId != null ? `id:${row.obstacleId}` : `name:${row.obstacleName}`;
        assessed.add(key);
        if (row.isCritical === true) critical.add(key);
      }
    }
  }
  return { obstaclesAssessed: assessed.size, criticalObstacles: critical.size };
}

export function gnssHasObstacleResults(result) {
  return (result?.segments || []).some(segment =>
    (Array.isArray(segment.obsRows) && segment.obsRows.length > 0)
    || Object.values(segment.missedApproach?.phaseResults || {})
      .some(rows => Array.isArray(rows) && rows.length > 0));
}

export function gnssResultSegments(segments, swedish = false) {
  return (segments || []).flatMap(segment => {
    const missed = segment.missedApproach;
    const phases = missed?.phaseResults;
    if (segment.missedResultPhase || !phases || String(segment.segFamily).toLowerCase() !== 'missed') return [segment];
    const phaseOrder = ['initial', 'intermediate', 'turn_initial', 'final'];
    if (!phaseOrder.some(phase => Array.isArray(phases[phase]) && phases[phase].length)) return [segment];
    const turns = missed.turns || [];
    const turnStart = turns[0]?.from || segment.toPhase || 'ETP';
    const turnEnd = turns[turns.length - 1]?.to || 'MISAP';
    const labels = {
      initial: 'Initial missed approach', intermediate: 'Intermediate missed approach',
      turn_initial: swedish ? 'Missed approach – svänginitieringsområde (TIA)' : 'Missed approach – turn initiation area (TIA)',
      final: 'Final missed approach',
    };
    const route = {
      initial: [segment.fromPhase || 'MAPT', 'SOC'],
      intermediate: [segment.fromPhase === 'MAPT' ? 'SOC' : segment.fromPhase || 'SOC', turnStart], turn_initial: ['ETP', turnStart], final: ['ETP', turnEnd],
    };
    return phaseOrder.filter(phase => Array.isArray(phases[phase]) && phases[phase].length).map(phase => {
      const rows = phases[phase];
      const review = rows.some(row => row.manualReviewRequired || row.areaType === 'conservative_review');
      return { ...segment, segLabel: labels[phase], missedResultPhase: phase,
        fromPhase: route[phase][0], toPhase: route[phase][1], moc: null,
        ifTurn: undefined, manualReviewRequired: review,
        obsRows: rows, criticalCount: rows.filter(row => row.isCritical).length };
    });
  });
}

export function gnssMocLabel(segment, swedish = false) {
  if (segment.lpvSurface || segment.xlsSurface) return 'OAS surface';
  const perObstacle = swedish ? 'MOC per hinder' : 'MOC per obstacle';
  if (String(segment.segFamily).toLowerCase() === 'missed' && segment.missedApproach) {
    const values = (segment.obsRows || []).map(row => row.mocM).filter(Number.isFinite);
    if (values.length && values.every(value => Math.abs(value - values[0]) < 0.005)) {
      const value = Number(values[0].toFixed(2));
      return `${swedish ? 'Tillämpad MOC' : 'Applied MOC'} ${value} m`;
    }
    return perObstacle;
  }
  if (segment.baroSurface) return perObstacle;
  return segment.moc == null ? 'Climb assessment' : `MOC ${segment.moc} m`;
}

export function gnssMissedParameters(segment, swedish = false) {
  const missed = segment.missedApproach;
  if (!segment.missedResultPhase || !missed) return [];
  const format = (value, digits = 1) => Number.isFinite(value) ? value.toFixed(digits) : '—';
  const pairs = [
    ['SOC', `${format(missed.socAltitudeFt)} ft AMSL | ${format(missed.socDistanceM)} m`],
    ['MISAP MACG', `${format(missed.macgPercent, 2)}%`],
  ];
  if (['final', 'turn_initial'].includes(segment.missedResultPhase)) {
    const indices = new Set((segment.obsRows || []).map(row => row.turnIndex).filter(Number.isFinite));
    for (const [index, turn] of (missed.turns || []).entries()) {
      if (indices.size && !indices.has(index + 1)) continue;
      const config = turn.turnConfig || {};
      pairs.push([`MISAP ${swedish ? 'ben' : 'leg'} ${turn.legNo ?? index + 2} (${turn.from || 'TP'} → ${turn.to || 'MISAP'})`,
        `${turn.legType || ''} | IAS ${format(config.iasKt)} kt | TAS ${format(turn.tasKt)} kt`
        + ` | ${swedish ? 'TAS-höjd' : 'TAS altitude'} ${format(config.altitudeFt)} ft AMSL`
        + ` | Bank nom/area ${format(config.bankNomDeg)}° / ${format(config.bankDeg)}°`
        + ` | ${swedish ? 'Vind' : 'Wind'} ${format(config.windKt)} kt`]);
      const review = turn.conservativeReview || turn.endJoinReview;
      if (review) pairs.push([swedish ? 'Granskningsytans referenshöjd' : 'Review area reference altitude',
        `${format(review.referenceAltitudeM)} m AMSL; ${swedish ? 'ingen ytterligare höjdmarginal från stiggradienten tillgodoräknas' : 'no additional climb credit'}`]);
    }
  }
  return pairs;
}
